import debounce from 'lodash/debounce'
import path from 'path'
import CodeMirror from 'codemirror'
import pick from 'lodash/pick'

export default {
  data() {
    const configs = this.$attrs.value
    const lastConfig = configs.length ? configs[configs.length - 1] : null
    const backups = {}
    configs.forEach(config => {
      backups[config.id || config.name] = config.config || config.text
    })
    return {
      activeId: lastConfig ? lastConfig.id || lastConfig.name : null,
      addingConfig: false,
      backups,
      previewing: false,
      selections: [],
    }
  },
  computed: {
    count() {
      return this.$attrs.value.length
    },
    active() {
      return this.activeId ? this.$attrs.value.findIndex(config => config.id === this.activeId || config.name === this.activeId) : null
    },
    activeConfig() {
      return this.$attrs.value[this.active] || null
    },
    activeError() {
      return this.error && this.error[this.activeId] || {}
    },
    configVariables() {
      const TLS13 = `TLS13-CHACHA20-POLY1305-SHA256:TLS13-AES-256-GCM-SHA384:TLS13-AES-128-GCM-SHA256`
      const TLS12 = `ECDHE-RSA-AES256-GCM-SHA512:DHE-RSA-AES256-GCM-SHA512:ECDHE-ECDSA-AES128-GCM-SHA256:ECDHE-ECDSA-CHACHA20-POLY1305:ECDHE-RSA-AES256-GCM-SHA384:ECDHE-RSA-AES128-GCM-SHA256:ECDHE-RSA-CHACHA20-POLY1305-SHA256:DHE-RSA-AES256-GCM-SHA384:DHE-RSA-AES128-GCM-SHA256:ECDHE-RSA-AES256-SHA384`
      const TLS11 = `ECDHE-RSA-AES256-SHA:ECDHE-RSA-AES128-SHA:DHE-RSA-AES256-SHA:DHE-RSA-AES128-SHA`
      const CIPHERS_HIGH = [TLS13, TLS12].join(':')
      const CIPHERS_MEDIUM = [TLS13, TLS12, TLS11].join(':')

      // current service variables
      const entity = this.entity
      const hasPassowrd = entity.password || entity.hasPassword
      const $name = entity.name ? entity.name.toLowerCase() : ''
      const $mainPort = entity.ports && entity.ports[0] && entity.ports[0].value || ''
      const $secondPort = entity.ports && entity.ports[1] && entity.ports[1].value || ''
      const $sslPort = $secondPort
      const $thirdPort = entity.ports && entity.ports[2] && entity.ports[2].value || ''

      // special variables
      const $memcachedAuth = hasPassowrd ? '-S' : '# -S'
      const $mongoAuth = hasPassowrd ? 'enabled' : 'disabled'
      const $mongoKeyfile = entity.configuration === 'ReplicaSet' ? 'keyFile: /data/configdb/replica.key' : ''
      const $mongoReplication = entity.configuration === 'ReplicaSet' ? `replication:\n  replSetName: ${entity.name}` : ''
      const $redisAuth = hasPassowrd ? `requirepass *********` : ''
      const $zeroRTT = parseFloat(entity.version) < 1.15 ? 'ssl_ecdh_curve secp521r1:secp384r1;' : 'ssl_early_data on;'

      // children service variables
      const service = this.activeService
      const config = this.activeConfig
      const isHTTPS = config && config.https !== 'none'
      const ownHTTPS = config && config.https === 'own'
      const hasDomains = Boolean(config && config.domains && config.domains.length)
      const $servicePort = service && service.ports && service.ports[0] && service.ports[0].value || ''
      const $serviceName = service && service.name && service.name.toLowerCase() || ''
      const $serviceDomains = hasDomains ? config.domains.join(' ') : '_'
      const $serviceNginxCert = (service && isHTTPS && hasDomains) ? (ownHTTPS ? `/etc/nginx/conf.d/${service.id}.pem` : `/etc/letsencrypt/live/${config.domains[0]}/fullchain.pem`) : ''
      const $serviceNginxKey = (service && isHTTPS && hasDomains) ? (ownHTTPS ? `/etc/nginx/conf.d/${service.id}.key` : `/etc/letsencrypt/live/${config.domains[0]}/privkey.pem`) : ''
      const $serviceHaproxyCert = (isHTTPS && hasDomains) ? (ownHTTPS ? '/usr/local/etc/haproxy/haproxy.pem' : `/etc/letsencrypt/live/${config.domains[0]}/combined.pem`) : ''
      const $serviceSSLProtocols = isHTTPS ? (config.secure === 'medium' ? 'TLSv1.1 TLSv1.2 TLSv1.3' : 'TLSv1.2 TLSv1.3') : ''
      const $serviceSSLCiphers = isHTTPS ? (config.secure === 'medium' ? CIPHERS_MEDIUM : CIPHERS_HIGH) : ''

      const $serviceHaproxyGlobal = isHTTPS ? `tune.ssl.default-dh-param 2048
  ssl-engine rdrand
  ssl-mode-async
  ssl-default-server-ciphers ${$serviceSSLCiphers}
  ssl-default-server-options no-sslv3 no-tlsv10 no-tls-tickets
  ssl-default-bind-ciphers ${$serviceSSLCiphers}
  ssl-default-bind-options no-sslv3 no-tlsv10 no-tls-tickets` : ''

      const $serviceHaproxyHTTPS = isHTTPS ? `bind 0.0.0.0:${$sslPort} ssl crt ${$serviceHaproxyCert} alpn h2,http/1.1 allow-0rtt
  http-request set-header X-Forwarded-Proto https
  redirect scheme https if !{ ssl_fc }
  http-response set-header Strict-Transport-Security max-age=15768000` : ''

      const $serviceNginxHTTPS = isHTTPS ? `server_name ${$serviceDomains};
  include conf.d/letsencrypt.inc;
  # Redirect all HTTP requests to HTTPS with a 301 Moved Permanently response.
  return 301 https://$host$request_uri;
}

server {
  listen ${$sslPort} ssl http2;

  ssl_certificate ${$serviceNginxCert};
  ssl_certificate_key ${$serviceNginxKey};

  ssl_session_timeout 1d;
  ssl_session_cache shared:SSL:50m;
  ssl_session_tickets off;
  ssl_buffer_size 1400;

  # Diffie-Hellman parameter for DHE ciphersuites
  ssl_dhparam /d2c/ssl/dhparam.pem;

  ssl_protocols ${$serviceSSLProtocols};
  ssl_ciphers "${$serviceSSLCiphers}";
  ssl_prefer_server_ciphers on;
  ${$zeroRTT}

  # HSTS (15768000 seconds = 6 months)
  add_header Strict-Transport-Security "max-age=15768000;preload";

  # OCSP Stapling ---
  # fetch OCSP records from URL in ssl_certificate and cache them
  ssl_stapling on;
  ssl_stapling_verify on;` : ''

      let $serviceProxyList = config && config.sticky ? 'cookie SERVERID insert indirect nocache\n  ' : ''
      $serviceProxyList += service ? service._containers.map((container, i) => `server srv${i} ${container.name}:${$servicePort} check${config.sticky ? ` cookie srv${i + 1}` : ''} resolvers dns resolve-prefer ipv4`).join('\n  ') : ''

      return {
        // all services
        $name,
        $mainPort,
        $secondPort,
        $sslPort,
        $thirdPort,
        // databases
        $memcachedAuth,
        $mongoAuth,
        $mongoKeyfile,
        $mongoReplication,
        $redisAuth,
        // nginx
        $serviceDomains,
        $serviceNginxCert,
        $serviceNginxHTTPS,
        $serviceNginxKey,
        $serviceSSLProtocols,
        // haproxy
        $serviceHaproxyCert,
        $serviceHaproxyGlobal,
        $serviceHaproxyHTTPS,
        $serviceProxyList,
        $zeroRTT,
        // nginx & haproxy
        $serviceName,
        $servicePort,
        $serviceSSLCiphers,
      }
    },
    entityConfigVariables() {
      return this.fields.configs ? pick(this.configVariables, this.fields.configs.variables) : {}
    },
    serviceConfigVariables() {
      return this.fields.services ? pick(this.configVariables, this.fields.services.serviceConfigVariables) : {}
    },
    editorOptions() {
      const fileName = path.basename(this.activeConfig ? this.activeConfig.name : '')
      const meta = CodeMirror.findModeByFileName(fileName)
      return {
        mode: this.field.syntax || (meta ? meta.mode : 'text'),
        viewportMargin: Infinity,
        lineNumbers: true,
        readOnly: false,
      }
    },
    previewOptions() {
      return {...this.editorOptions, readOnly: true}
    },
    preview() {
      let preview = ''
      if (this.activeConfig) {
        preview = this.activeConfig.config || this.activeConfig.text || ''
        for (const variable in this.configVariables) {
          const regexp = new RegExp(`\\${variable}`, 'g')
          preview = preview.replace(regexp, this.configVariables[variable])
        }
      }
      return preview
    },
    hasChanges() {
      return this.activeConfig && (this.backups[this.activeId] && this.backups[this.activeId] !== this.$attrs.value[this.active][this.contentField])
    },
    contentField() {
      return 'text' in this.activeConfig ? 'text' : 'config'
    },
  },
  watch: {
    count(newCount, oldCount) {
      if (oldCount < newCount) {
        // autoselect new configs
        const lastConfig = this.$attrs.value[newCount - 1]
        this.openConfig(lastConfig.id || lastConfig.name)
      }
    },
    active(newActive, oldActive) {
      // if we can't find activeId and there are some configs
      if (newActive === -1 && this.$attrs.value.length) {
        let newActiveConfig = null
        // open previous existed config
        while(!newActiveConfig) {
          oldActive = oldActive ? oldActive - 1 : 0
          newActiveConfig = this.$attrs.value[oldActive]
        }
        this.openConfig(newActiveConfig ? newActiveConfig.id || newActiveConfig.name : null)
      }
    },
    previewing(newValue) {
      this.$nextTick(() => {
        if (!newValue && this.$refs.editor && this.$refs.editor.cminstance) {
          this.$refs.editor.cminstance.setSelections(this.selections)
        }
      })
    },
  },
  methods: {
    focus() {
      this.$nextTick(() => {
        if (this.$refs.editor && this.$refs.editor.cminstance) {
          this.$refs.editor.cminstance.focus()
        }
      })
    },
    openConfig(configId) {
      this.generator = null
      this.selections = []
      this.activeId = configId
      this.previewing = false
      if (this.$refs.editor && this.$refs.editor.cminstance) {
        this.$refs.editor.cminstance.scrollTo(null, 0);
      }
    },
    setConfigField(field, value) {
      this.$attrs.value[this.active][field] = value
      this.onInput()
    },
    cancelChanges() {
      this.setConfigField(this.contentField, this.backups[this.activeId] || '')
      this.focus()
    },
    deleteConfig(configId) {
      const index = this.$attrs.value.findIndex(config => config.id === configId || config.name === configId)
      this.$attrs.value.splice(index, 1)
      if (this.activeId !== configId && this.previewing) {
        this.focus()
      }
      this.onInput()
    },
    onFocus(codemirror) {
      codemirror.refresh()
    },
    onSelect() {
      this.$nextTick(() => {
        const $codemirror = this.$refs.editor || this.$refs.preview
        this.selections = $codemirror ? $codemirror.cminstance.listSelections() : []
      })
    },
    debouncedOnSelect: debounce(function() {
      this.onSelect()
    }, 300),
  },
}
